<script lang="ts">
export default {
  name: 'Step3EpicCleanup',
};
</script>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watchEffect } from 'vue';
import { Clock, Info } from '@lucide/vue';
import { useDataReadinessWizard } from '@/composables/useDataReadinessWizard';
import EpicRuleAccordion, { type EpicRuleKey } from '@/components/v2/data_readiness/EpicRuleAccordion.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import { relativeDateTime } from '@/utilities/Dates';
import { countEpicIssues } from '@/utilities/dataReadinessIssues';

const wizard = useDataReadinessWizard();

// Initial load only — a refetch with data already on screen keeps it visible.
const isLoading = computed(() => wizard.loadingRules.value && wizard.ruleViolatingPolicies.value.length === 0);

onBeforeMount(async () => {
  await Promise.all([wizard.refreshRuleViolatingPolicies(), wizard.refreshLastSync()]);
});

const invalidPolicyNumberPolicies = computed(() => wizard.ruleViolatingPolicies.value.filter((p) => p.invalidPolicyNumber));
const noPremiumOrFeePolicies = computed(() => wizard.ruleViolatingPolicies.value.filter((p) => p.invalidMissingPremiumOrFees));
const invalidDatesPolicies = computed(() => wizard.ruleViolatingPolicies.value.filter((p) => p.invalidDates));

const totalIssues = computed(() => countEpicIssues(wizard.ruleViolatingPolicies.value));

const openRule = ref<EpicRuleKey | null>(null);
function toggleRule(key: EpicRuleKey) {
  openRule.value = openRule.value === key ? null : key;
}

const lastSyncText = computed(() => {
  const ts = wizard.lastEpicSync.value?.lastSyncedAt;
  if (!ts) return 'not yet synced';
  return relativeDateTime(ts) ?? ts;
});

watchEffect(() => {
  if (isLoading.value) {
    wizard.setFooterHint({ text: 'Checking policies…', color: 'muted' });
  } else if (totalIssues.value === 0) {
    wizard.setFooterHint({ text: 'All Epic checks clear — ready to proceed', color: 'green' });
  } else {
    wizard.setFooterHint({
      text: `${totalIssues.value} issue${totalIssues.value === 1 ? '' : 's'} need${totalIssues.value === 1 ? 's' : ''} correction in Epic`,
      color: 'amber',
    });
  }
});

onBeforeUnmount(() => wizard.setFooterHint(null));
</script>

<template>
  <div class="p-6">
    <h2 class="mb-1 text-base font-semibold">Epic cleanup</h2>
    <p class="mb-4 text-sm text-base-content/60">
      The checks below flag data quality issues that must be corrected directly in Epic. Changes sync to the platform overnight — return the next
      business day to confirm issues are resolved.
    </p>

    <!-- Persistent info banner (soft styling to match the wizard's readiness banners) -->
    <div class="mb-4 flex items-start gap-3 rounded-md border border-info/20 bg-info/10 px-4 py-3 text-sm">
      <Info
        :size="18"
        class="mt-0.5 shrink-0 text-info" />
      <span class="text-base-content/80">
        Corrections must be made in <strong>Epic</strong>. This platform reflects the previous night's sync. If you've already made corrections,
        check back tomorrow to see the updated status.
      </span>
    </div>

    <!-- Last-sync line -->
    <div class="mb-4 flex items-center gap-1 text-xs text-base-content/50">
      <Clock :size="13" />
      <span>Last synced from Epic:</span>
      <strong class="ml-1 text-base-content/70">{{ lastSyncText }}</strong>
    </div>

    <!-- Accordion -->
    <div
      v-if="isLoading"
      class="overflow-hidden rounded-md border border-base-300">
      <div
        v-for="i in 3"
        :key="`skeleton-${i}`"
        class="flex items-center gap-3 border-b border-base-300 px-4 py-3 last:border-b-0">
        <Skeleton
          shape="circle"
          size="sm" />
        <div class="flex flex-col gap-1.5">
          <Skeleton width="10rem" />
          <Skeleton
            width="16rem"
            height="0.6rem" />
        </div>
      </div>
    </div>
    <div
      v-else
      class="overflow-hidden rounded-md border border-base-300">
      <EpicRuleAccordion
        rule-key="invalidPolicyNumber"
        title="Invalid policy number"
        criteria='Blank, "TBD", "Do Not Use", 25+ characters, or insurer name'
        :policies="invalidPolicyNumberPolicies"
        :is-open="openRule === 'invalidPolicyNumber'"
        @toggle="toggleRule('invalidPolicyNumber')" />
      <EpicRuleAccordion
        rule-key="noPremiumOrFee"
        title="No premium or fee"
        criteria="Active policies where no premium amount or broker fee has been entered — the required fix depends on Bill Mode"
        :policies="noPremiumOrFeePolicies"
        :is-open="openRule === 'noPremiumOrFee'"
        @toggle="toggleRule('noPremiumOrFee')" />
      <EpicRuleAccordion
        rule-key="invalidDates"
        title="Invalid dates"
        criteria="Missing effective or expiration date, or expiration before effective date"
        :policies="invalidDatesPolicies"
        :is-open="openRule === 'invalidDates'"
        @toggle="toggleRule('invalidDates')" />
    </div>
  </div>
</template>
